import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { hrmsGetRequest, hrmsPostRequest, hrmsUpdateRequest, hrmsDeleteRequest } from "../services/index";

const API_URL = "/api/attendance";

export const fetchAttendance = createAsyncThunk(
  "attendance/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(API_URL);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAttendanceByEmail = createAsyncThunk(
  "attendance/fetchByEmail",
  async (email, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/email/${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAttendanceByDate = createAsyncThunk(
  "attendance/fetchByDate",
  async (date, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/date/${encodeURIComponent(date)}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkAttendanceStatus = createAsyncThunk(
  "attendance/checkStatus",
  async ({ email, date }, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/status/${encodeURIComponent(email)}/${date}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkBulkAttendanceStatus = createAsyncThunk(
  "attendance/checkBulkStatus",
  async ({ emails, date }, { rejectWithValue }) => {
    try {
      const response = await hrmsPostRequest(`${API_URL}/bulk-status`, { emails, date });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// New async thunk for enhanced bulk attendance status with leave information
export const checkBulkAttendanceStatusWithLeaveInfo = createAsyncThunk(
  "attendance/checkBulkStatusWithLeaveInfo",
  async ({ emails, date }, { rejectWithValue }) => {
    try {
      const response = await hrmsPostRequest(`${API_URL}/bulk-status-with-leave`, { emails, date });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// New async thunk to get attendance with leave information
export const getAttendanceWithLeaveInfo = createAsyncThunk(
  "attendance/getWithLeaveInfo",
  async ({ email, date }, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/with-leave-info/${encodeURIComponent(email)}/${date}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addAttendance = createAsyncThunk(
  "attendance/add",
  async (attendanceData, { rejectWithValue }) => {
    try {
      const response = await hrmsPostRequest(API_URL, attendanceData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateAttendance = createAsyncThunk(
  "attendance/update",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(`${API_URL}/${id}`, updatedData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addBreak = createAsyncThunk(
  "attendance/addBreak",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await hrmsPostRequest(`${API_URL}/${id}/break`, {});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const endBreak = createAsyncThunk(
  "attendance/endBreak",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(`${API_URL}/${id}/break`, {});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAttendance = createAsyncThunk(
  "attendance/delete",
  async (id, { rejectWithValue }) => {
    try {
      await hrmsDeleteRequest(`${API_URL}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const attendanceSlice = createSlice({
  name: "attendance",
  initialState: {
    attendanceRecords: [],
    status: "idle",
    error: null,
    bulkStatusResults: {},
  },
  reducers: {
    clearAttendance: (state) => {
      state.attendanceRecords = [];
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAttendance.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAttendance.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.attendanceRecords = action.payload;
      })
      .addCase(fetchAttendance.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch attendance";
      })
      .addCase(fetchAttendanceByEmail.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAttendanceByEmail.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.attendanceRecords = action.payload;
      })
      .addCase(fetchAttendanceByEmail.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch attendance by email";
      })
      .addCase(fetchAttendanceByDate.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAttendanceByDate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.attendanceRecords = action.payload;
      })
      .addCase(fetchAttendanceByDate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch attendance by date";
      })
      .addCase(checkAttendanceStatus.pending, (state) => {
        state.status = "loading";
      })
      .addCase(checkAttendanceStatus.fulfilled, (state, action) => {
        state.status = "succeeded";
        // The action payload is the status, so we can add it to the state if needed
        // For now, we'll just update the status
      })
      .addCase(checkAttendanceStatus.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to check attendance status";
      })
      .addCase(checkBulkAttendanceStatus.pending, (state) => {
        state.status = "loading";
      })
      .addCase(checkBulkAttendanceStatus.fulfilled, (state, action) => {
        state.status = "succeeded";
        // Store bulk status results
        state.bulkStatusResults = action.payload;
      })
      .addCase(checkBulkAttendanceStatus.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to check bulk attendance status";
      })
      .addCase(addAttendance.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addAttendance.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.attendanceRecords.push(action.payload);
      })
      .addCase(addAttendance.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to add attendance";
      })
      .addCase(updateAttendance.fulfilled, (state, action) => {
        const index = state.attendanceRecords.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.attendanceRecords[index] = action.payload;
        }
      })
      .addCase(addBreak.fulfilled, (state, action) => {
        const index = state.attendanceRecords.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.attendanceRecords[index] = action.payload;
        }
      })
      .addCase(endBreak.fulfilled, (state, action) => {
        const index = state.attendanceRecords.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.attendanceRecords[index] = action.payload;
        }
      })
      .addCase(deleteAttendance.fulfilled, (state, action) => {
        state.attendanceRecords = state.attendanceRecords.filter((a) => a.id !== action.payload);
      });
  },
});

export const { clearAttendance } = attendanceSlice.actions;
export default attendanceSlice.reducer;